/**
 * Identifiants `data-testid` — source de vérité unique.
 *
 * Le même attribut est consommé à trois endroits : le JSX du composant, le
 * `demo_script` d'un `FeatureManifest` et la suite Playwright (runner +
 * `global-setup`). Les figer ici, plutôt que de répéter des littéraux, garantit
 * qu'un renommage casse la compilation TypeScript — au lieu de casser un test
 * en silence (l'élément n'est simplement plus trouvé).
 *
 * Cf. docs/documentation-strategy.md § 10.7.
 */
export const TEST_IDS = {
  /** Bouton « Ajouter + » de la liste des clients (`/customers`). */
  customers_add: 'customers-add',
  /** Bouton « Ajouter + » de la liste des prestations (`/catalog/prestations`). */
  prestations_add: 'prestations-add',
  /** Bouton « Ajouter » de la liste des lieux (`/establishment/place`). */
  places_add: 'places-add',
  /** Bouton « Ajouter » de la barre d'outils de l'agenda (`/agenda`). */
  agenda_add_appointment: 'agenda-add-appointment',
  /** Bouton de validation d'un `FullScreenModalUpsert`. */
  modal_submit: 'modal-submit',
  /** Bouton de soumission d'un `DynamicForm`. */
  dynamic_form_submit: 'dynamic-form-submit',
  /** Champ « nom » du formulaire de rendez-vous (`AppointmentForm`). */
  appointment_name: 'appointment-name',
  /** Bouton de validation du formulaire de rendez-vous (`AppointmentFormFooter`). */
  appointment_submit: 'appointment-submit',
  /** Déclencheur du menu compte (footer de la sidebar) qui contient la déconnexion. */
  sidebar_account_menu: 'sidebar-account-menu',
  /** Item « Déconnexion » du menu compte. */
  sidebar_logout: 'sidebar-logout',
  /** Item « Créer une entreprise » du sélecteur d'entreprise de la sidebar. */
  sidebar_create_company: 'sidebar-create-company',
  /** Bouton de validation de la modale de création d'entreprise (`CreateCompanyModal`). */
  create_company_submit: 'create-company-submit',
  /** Conteneur principal de la grille de l'agenda (`/agenda`). */
  agenda_calendar: 'agenda-calendar',
  /** Déclencheur du menu « Disponibilités » de la barre d'outils de l'agenda. */
  agenda_availabilities_menu: 'agenda-availabilities-menu',
  /** Bouton de validation du dialog de blocage / restauration de disponibilités. */
  availability_range_submit: 'availability-range-submit',
  /**
   * Bouton de confirmation de la carte qui s'affiche APRÈS « Valider » dans le
   * dialog de blocage — partagé par les deux chemins (`ConfirmDialog` sans RDV
   * en conflit, `MarkUnavailableDialog` avec RDV en conflit).
   */
  availability_range_confirm: 'availability-range-confirm',
  /** Bouton d'ouverture de la recherche d'une liste (`ListedItemFullPage`). */
  list_search: 'list-search',
  /**
   * Déclencheur du menu « Trier » d'une liste (`ListSortButton` dans `ListedItemFullPage`). Les
   * options se visent avec `optionTestId(TEST_IDS.list_sort, index)` (option par défaut = index 0).
   */
  list_sort: 'list-sort',
  /** Bouton crayon « Modifier » d'une ligne de liste (`ListedItemFullPage`). */
  list_row_edit: 'list-row-edit',
  /**
   * Ligne d'une liste (`ListedItemFullPage`). Posé sur chaque ligne : viser
   * `.first()` pour ouvrir le premier élément (ordre déterministe garanti par le
   * seed E2E).
   */
  list_row: 'list-row',
  /** Item « Rechercher » de la sidebar — ouvre la recherche globale (`SearchComponent`). */
  sidebar_search: 'sidebar-search',
  /** Bouton « Créer une campagne + » de la liste marketing (`/marketing/campaigns`). */
  campaigns_create: 'campaigns-create',
  /**
   * Bouton « Nouvelle promotion + » de la page Marketing › Promotions (`/marketing/promotions`). Liste
   * vide, l'état vide propose aussi de créer : viser ce testid, pas le nom du bouton.
   */
  promotions_create: 'promotions-create',
  /** Champ « objet » de l'éditeur de campagne email. */
  campaign_subject: 'campaign-subject',
  /** Conteneur racine de l'éditeur d'email (`EmailBuilderWorkspace`). */
  email_builder: 'email-builder',
  /** Champ de saisie de la recherche (`SearchComponent` / `CommandInput`). */
  search_input: 'search-input',
  /** Déclencheur du sélecteur de prestations (`PrestationPicker` dans `AppointmentForm`). */
  prestation_picker: 'prestation-picker',
  /** Stepper « Personnes » d'une ligne de prestation (`PrestationPicker`, agenda pro). */
  prestation_party_size_decrease: 'prestation-party-size-decrease',
  prestation_party_size_increase: 'prestation-party-size-increase',
  prestation_party_size_value: 'prestation-party-size-value',
  /** Bloc « règle de montant » d'une prestation (`PaymentAmountRuleInput`, formulaire et modification en masse) : bascule % / montant fixe (`-0` / `-1`) et saisie du pourcentage. */
  prestation_payment_rule_mode: 'prestation-payment-rule-mode',
  prestation_payment_rule_percentage: 'prestation-payment-rule-percentage',
  /** « Retirer ce créneau » d'une réservation multi-créneaux (`EditAppointmentSheet`). */
  appointment_remove_slot: 'appointment-remove-slot',
  /** Portée « Ce créneau uniquement » du dialog d'annulation (`cancel-appointment-action`). */
  appointment_cancel_scope_this_slot: 'appointment-cancel-scope-this-slot',
  /** Aperçu des créneaux consécutifs d'un rendez-vous multi-prestations (`AppointmentForm`). */
  appointment_chain_preview: 'appointment-chain-preview',
  /** Bouton « Modèles » de l'éditeur d'email (`EmailTemplatePicker`). */
  email_template_picker: 'email-template-picker',
  /** Champ de recherche d'activité (`ActivityPicker`, modale profil). */
  activity_picker_search: 'activity-picker-search',
  /** Bouton « Explorer les activités » (`ActivityPicker`). */
  activity_picker_explore_toggle: 'activity-picker-explore-toggle',
  /** Bouton trigger Pulse ✨ d'un champ AI (`AiFieldButton`). */
  ai_field_trigger: 'ai-field-trigger',
  /** Textarea de saisie d'un prompt libre dans le popover Pulse (`AiFieldButton`). */
  ai_field_input: 'ai-field-input',
  /** Bouton « Envoyer » du popover Pulse (`AiFieldButton`). */
  ai_field_send: 'ai-field-send',
  /** Bouton « Confirmer le recadrage » du `CropDialog` (apparaît après upload d'une image à crop). */
  crop_confirm: 'crop-confirm',
  /** Conteneur des paramètres de cartes cadeaux (`/catalog/gift-cards`). */
  gift_card_settings: 'gift-card-settings',
  /** Bouton « Ajouter une catégorie » de la liste des prestations (`/catalog/prestations`). */
  category_add: 'category-add',
  /** Bouton « Suppléments » de la liste des prestations qui mène à `/catalog/supplement`. */
  catalog_supplements_open: 'catalog-supplements-open',
  /** Bouton « Ajouter + » de la liste des suppléments (`/catalog/supplement`). */
  supplements_add: 'supplements-add',
  /** Bouton « Matériel » de la liste des prestations qui mène à `/catalog/equipment`. */
  catalog_equipment_open: 'catalog-equipment-open',
  /** Badge « Réservabilité » d'une prestation du catalogue (`data-status` = statut, P3). */
  prestation_bookability_badge: 'prestation-bookability-badge',
  /** Encart « Cette prestation n'apparaît pas dans votre tunnel » de la fiche prestation. */
  prestation_not_in_tunnel_notice: 'prestation-not-in-tunnel-notice',
  /** « Ajouter à un planning » / « Voir les plannings » (badge et encart) : ouvre la fenêtre des plannings. */
  prestation_bookability_open_plannings: 'prestation-bookability-open-plannings',
  /** Bouton « Ajouter + » de la liste des ressources / matériel (`/catalog/equipment`). */
  equipment_add: 'equipment-add',
  /** Bouton « Nouveau pack » de la liste des packs (`/catalog/packs`). */
  packs_add: 'packs-add',
  /** Bouton « Personnaliser la carte » des cartes cadeaux (`/catalog/gift-cards`). */
  gift_card_customize: 'gift-card-customize',
  /** Carte « Profil » de l'onglet Informations du hub établissement (`/establishment`). */
  establishment_card_profile: 'establishment-card-profile',
  /** Carte « Photos & Logo » de l'onglet Informations (`/establishment`). */
  establishment_card_media: 'establishment-card-media',
  /** Carte « Équipe » de l'onglet Informations (`/establishment`). */
  establishment_card_team: 'establishment-card-team',
  /** Carte « Lieux » de l'onglet Informations (`/establishment`). */
  establishment_card_place: 'establishment-card-place',
  /** Carte « Synchronisation Google » de l'onglet Paramètres (`/establishment/parameters`). */
  establishment_card_google_sync: 'establishment-card-google-sync',
  /** Carte « Personnaliser Pulse » de l'onglet Paramètres (`/establishment/parameters`). */
  establishment_card_pulse: 'establishment-card-pulse',
  /** Carte « Paiements / Stripe » de l'onglet Comptes de paiements (`/establishment/payments`). */
  establishment_card_payment: 'establishment-card-payment',
  /** Bouton « Ajouter un membre » de la liste d'équipe (`/establishment/team`). */
  team_add: 'team-add',
  /** Bouton « Connecter Google » de la synchronisation GMB (`/establishment/gmb`). */
  gmb_connect_google: 'gmb-connect-google',
  /** Bouton « Connecter mon compte Stripe » (`/establishment/stripe-connect`). */
  stripe_connect_button: 'stripe-connect-button',
  /** Bouton « Ajouter + » de la liste des tags clients (`/customers/tags`). */
  tags_add: 'tags-add',
  /** Bouton « Tags » de la liste des clients qui mène à la gestion des tags (`/customers`). */
  customers_tags_nav: 'customers-tags-nav',
  /** Bouton « Vérifier » de la bannière doublons de la liste des clients (`/customers`). */
  customers_duplicates_open: 'customers-duplicates-open',
  /** Bouton « Comparer et fusionner » d'une paire de doublons (modale doublons + encart fiche). */
  customers_duplicate_compare: 'customers-duplicate-compare',
  /** Bouton « Ce n'est pas un doublon » d'une paire de doublons. */
  customers_duplicate_ignore: 'customers-duplicate-ignore',
  /** Sélecteur « Conserver la fiche A / B » de l'aperçu de fusion. */
  customers_merge_swap: 'customers-merge-swap',
  /** Bouton « Fusionner » de l'aperçu de fusion. */
  customers_merge_submit: 'customers-merge-submit',
  /** Bouton de confirmation finale de la fusion (ConfirmDialog). */
  customers_merge_confirm: 'customers-merge-confirm',
  /** Lien « Ouvrir la fiche existante » du hint doublon du formulaire de création client. */
  customers_duplicate_open_existing: 'customers-duplicate-open-existing',
  /** Bouton « Créer une publication + » de la liste du blog (`/marketing/publications`). */
  publications_create: 'publications-create',
  /** Bouton « Gérer les catégories » du volet des publications (`/marketing/publications`). */
  publication_category_manage: 'publication-category-manage',
  /** Déclencheur « Ajouter une catégorie », dans la modale de gestion des catégories. */
  publication_category_add: 'publication-category-add',
  /** Bouton « Ajouter un domaine » du panneau de gestion de domaine (`/website/domain`). */
  domain_add: 'domain-add',
  /** Bouton « Paramètres » ouvrant la modale des paramètres de communication (`/marketing/campaigns`). */
  comm_settings_open: 'comm-settings-open',
  /** Conteneur de la liste des messages automatiques (`/marketing/notifications`). */
  messages_auto_list: 'messages-auto-list',
  /** Bouton « Modifier le périmètre » (déplie le choix local/national) (`/website/settings/seo`). */
  seo_scope_change: 'seo-scope-change',
  /** Bouton « Ajouter une règle » de redirection (`/website/settings/redirects`). */
  redirects_add_rule: 'redirects-add-rule',
  /** Bouton « Créer une adresse » du gestionnaire de boîtes mail (`/website/domain`). */
  mailbox_create: 'mailbox-create',
  /** Bouton « Créer » du dialog de création d'adresse email. */
  mailbox_create_submit: 'mailbox-create-submit',
  /** Carte d'un rendez-vous dans la grille de l'agenda (`DraggableEvent`). */
  agenda_event_card: 'agenda-event-card',
  /** Badge de statut affiché dans le détail d'un rendez-vous. */
  appointment_status_badge: 'appointment-status-badge',
  /** Lien « Voir la fiche client » du détail d'un rendez-vous. */
  appointment_view_customer: 'appointment-view-customer',
  /** Bouton « Modifier » du détail d'un rendez-vous. */
  appointment_edit: 'appointment-edit',
  /** Bouton rouge « Annuler le rendez-vous » du détail. */
  appointment_cancel: 'appointment-cancel',
  /** Bouton de confirmation d'annulation (`CancellationReasonDialog`). */
  appointment_cancel_confirm: 'appointment-cancel-confirm',
  /** Bouton « Client non présenté » du détail d'un rendez-vous passé. */
  appointment_no_show: 'appointment-no-show',
  /** Bouton « Retirer « non présenté » » du détail d'un rendez-vous marqué. */
  appointment_no_show_undo: 'appointment-no-show-undo',
  appointment_no_show_confirm: 'appointment-no-show-confirm',
  /** Badge « Non présenté » du détail d'un rendez-vous. */
  appointment_no_show_badge: 'appointment-no-show-badge',
  /** Badge « Non présenté » d'un rendez-vous passé dans la fiche client. */
  customer_appointment_no_show_badge: 'customer-appointment-no-show-badge',
  // Statistiques (page /statistics) et widgets d'accueil.
  stats_page: 'stats-page',
  stats_tab: 'stats-tab',
  stats_period_pill: 'stats-period-pill',
  stats_period_custom: 'stats-period-custom',
  stats_compare_toggle: 'stats-compare-toggle',
  stats_kpi_card: 'stats-kpi-card',
  stats_breakdown_dimension: 'stats-breakdown-dimension',
  stats_export: 'stats-export',
  stats_upsell: 'stats-upsell',
  stats_nudge: 'stats-nudge',
  stats_inactive_cta: 'stats-inactive-cta',
  home_today_widget: 'home-today-widget',
  home_week_widget: 'home-week-widget',
  home_to_handle_widget: 'home-to-handle-widget',
  /** Conteneur racine de l'éditeur de site (`/website/builder`). */
  website_builder_root: 'website-builder-root',
  /** Bouton engrenage « Paramètres de réservation » de la barre d'outils de l'agenda. */
  agenda_settings_button: 'agenda-settings-button',
  /** Bouton « Partager » de la barre d'outils de l'agenda. */
  agenda_share_button: 'agenda-share-button',
  /** Modale des paramètres de réservation (`/agenda/settings/...`). */
  agenda_settings_modal: 'agenda-settings-modal',
  /** Champ (lecture seule) du lien de réservation à partager (`/agenda/share`). */
  agenda_share_link_input: 'agenda-share-link-input',
  /** Bouton « Copier le lien » du partage de réservation (`/agenda/share`). */
  agenda_share_copy: 'agenda-share-copy',
  /** Bouton « Enregistrer » de la modale des paramètres de communication. */
  comm_settings_save: 'comm-settings-save',
  /** Modale de détail d'un paiement (`/sales` → clic sur une ligne). */
  payment_detail_modal: 'payment-detail-modal',
  /** Modale de détail d'une carte cadeau / d'un pack vendu (`/sales/coupons`). */
  voucher_detail_modal: 'voucher-detail-modal',
  /** Bouton « Scanner un coupon » de l'en-tête de `/sales/coupons` (libellé visible à partir de `md`). */
  voucher_scan_button: 'voucher-scan-button',
  /** Fenêtre de scan d'un coupon (`VoucherScanDialog`). */
  voucher_scan_dialog: 'voucher-scan-dialog',
  /** Champ « Code du coupon » de la fenêtre de scan. */
  voucher_scan_manual_input: 'voucher-scan-manual-input',
  /** Bouton « Rechercher » de la fenêtre de scan. */
  voucher_scan_manual_submit: 'voucher-scan-manual-submit',
  /** Liste des bons trouvés par nom de client dans la fenêtre de scan. */
  voucher_scan_search_results: 'voucher-scan-search-results',
  /** Une ligne de cette liste (un bon utilisable). */
  voucher_scan_search_result: 'voucher-scan-search-result',
  /** Récapitulatif du coupon en tête de l'étape d'utilisation (`VoucherUseDialog`). */
  voucher_use_summary: 'voucher-use-summary',
  /** Bouton « Modifier le site » de l'overview `/website` qui ouvre l'éditeur (`/website/builder`). */
  website_edit_builder: 'website-edit-builder',
  /** Carte « Email de contact » du hub Établissement (onglet Informations). */
  establishment_card_contact_email: 'establishment-card-contact-email',
  /** Adresse actuelle affichée sur les écrans de changement d'email (contact / connexion). */
  email_change_current: 'email-change-current',
  /** Bouton « Modifier » des écrans de changement d'email (grisé mais cliquable si non autorisé). */
  email_change_edit: 'email-change-edit',
  /** Action « Renvoyer le lien » du bandeau « changement d'email en attente ». */
  email_change_resend: 'email-change-resend',
  /** Action « Annuler la demande » du bandeau « changement d'email en attente ». */
  email_change_cancel_request: 'email-change-cancel-request',
  /** Bouton « Confirmer » / « Annuler la demande » des pages publiques ouvertes depuis l'email. */
  email_change_link_submit: 'email-change-link-submit',
  /** Mention « Nouvel email en attente » d'une ligne de la liste d'équipe. */
  team_member_pending_email: 'team-member-pending-email',
  /** Bouton « Annuler » le changement d'email de connexion d'une ligne de la liste d'équipe. */
  team_member_cancel_email_change: 'team-member-cancel-email-change',
} as const;

/** Union des valeurs de `TEST_IDS` — un `data-testid` statique connu. */
export type TestId = (typeof TEST_IDS)[keyof typeof TEST_IDS];

/**
 * `data-testid` d'un champ de `DynamicForm`. Le wrapper `FormItem` porte
 * `field-<clé>`, où `<clé>` est la clé du schéma TypeBox du champ.
 *
 * @example fieldTestId('email') // 'field-email'
 */
export const fieldTestId = (fieldKey: string): string => `field-${fieldKey}`;

/**
 * `data-testid` d'un champ à partir de son nom react-hook-form, sous sa forme
 * plate (`price`) ou indexée (`prices[0].price_infos`). Normalise les crochets,
 * volontairement fragiles en sélecteur, en tirets — le résultat coïncide donc
 * avec `fieldTestId` pour un champ plat et avec `arrayItemFieldTestId` pour une
 * ligne de tableau. Sert de RACINE aux testids des sous-éléments d'un champ
 * composite (montant, sélecteur…).
 *
 * @example fieldPathTestId('prices[0].price_infos') // 'field-prices-0-price_infos'
 */
export const fieldPathTestId = (fieldPath: string): string =>
  `field-${fieldPath.replace(/\[(\d+)\]/g, '.$1').replace(/\./g, '-')}`;

/**
 * `data-testid` du champ MONTANT d'un `PriceInput`. Un champ de prix rend deux
 * contrôles côte à côte (sélecteur de type PUIS montant) : viser le wrapper et
 * prendre le premier input tomberait sur le sélecteur. D'où un testid dédié.
 *
 * @example priceAmountTestId('prices[0].price_infos') // 'field-prices-0-price_infos-amount'
 */
export const priceAmountTestId = (fieldPath: string): string => `${fieldPathTestId(fieldPath)}-amount`;

/**
 * `data-testid` du déclencheur du sélecteur de TYPE de prix (fixe / à partir de
 * / sur devis) d'un `PriceInput`. Les options se visent avec `optionTestId`.
 *
 * @example priceTypeTestId('prices[0].price_infos') // 'field-prices-0-price_infos-type'
 */
export const priceTypeTestId = (fieldPath: string): string => `${fieldPathTestId(fieldPath)}-type`;

/**
 * `data-testid` du déclencheur d'un `MultiSelect` posé par `DynamicForm` pour un
 * champ à choix (enum, `anyOf`, liste). Les options du popover se visent avec
 * `optionTestId(selectTriggerTestId(...), index)`.
 *
 * @example selectTriggerTestId('type') // 'field-type-select'
 */
export const selectTriggerTestId = (fieldPath: string): string => `${fieldPathTestId(fieldPath)}-select`;

/**
 * `data-testid` d'une option d'`OptionButton`. Le bouton déclencheur porte son
 * propre `testid` ; chaque option du popover porte `<testid>-option-<index>`
 * (index 0-basé, dans l'ordre des options).
 *
 * @example optionTestId(TEST_IDS.agenda_add_appointment, 0) // 'agenda-add-appointment-option-0'
 */
export const optionTestId = (triggerTestId: string, index: number): string => `${triggerTestId}-option-${index}`;

/**
 * `data-testid` d'un bouton de préréglage de période du dialog de blocage de
 * disponibilités (`today` / `tomorrow` / `this_week` / `next_week`).
 *
 * @example availabilityRangePreset('tomorrow') // 'availability-range-preset-tomorrow'
 */
export const availabilityRangePreset = (preset: string): string => `availability-range-preset-${preset}`;

/**
 * `data-testid` d'un résultat de la recherche d'activités (`ActivityPicker`).
 * Chaque résultat porte `activity-picker-result-<index>` (index 0-basé, ordre
 * d'affichage des matches).
 *
 * @example activitySearchResultTestId(0) // 'activity-picker-result-0'
 */
export const activitySearchResultTestId = (index: number): string => `activity-picker-result-${index}`;

/**
 * `data-testid` du bouton « + » qui ajoute une ligne à un champ de type tableau
 * d'objets dans `DynamicForm` (ex : `external_urls`, `service_areas`).
 *
 * @example arrayAddTestId('external_urls') // 'field-external_urls-add'
 */
export const arrayAddTestId = (fieldName: string): string => `field-${fieldName}-add`;

/**
 * `data-testid` du wrapper `FormItem` d'un sous-champ d'une ligne d'un champ
 * tableau d'objets dans `DynamicForm`. Permet de viser un input nested
 * (`external_urls[0].url`) sans dépendre du nom react-hook-form qui contient
 * des crochets fragiles côté sélecteurs.
 *
 * @example arrayItemFieldTestId('external_urls', 0, 'url') // 'field-external_urls-0-url'
 */
export const arrayItemFieldTestId = (fieldName: string, index: number, subKey: string): string =>
  `field-${fieldName}-${index}-${subKey}`;

/**
 * `data-testid` de l'`<input type="file">` caché à l'intérieur d'un
 * `ImageUploadInput` posé par `DynamicForm` pour un champ image (`logo`,
 * `pictures`, etc.). Permet à Playwright d'attaquer le file picker via
 * `setInputFiles()` sans dépendre de la classe CSS.
 *
 * @example imageInputFileTestId('logo') // 'field-logo-file'
 */
export const imageInputFileTestId = (fieldName: string): string => `field-${fieldName}-file`;

/**
 * `data-testid` de la carte d'un événement de notification (`/marketing/notifications`).
 * Clé = `event_type` (ex. `booking_waiting_confirmation`).
 *
 * @example notificationEventCardTestId('booking_waiting_confirmation') // 'notification-event-booking_waiting_confirmation'
 */
export const notificationEventCardTestId = (eventType: string): string => `notification-event-${eventType}`;

/**
 * `data-testid` de l'interrupteur d'un canal (e-mail/SMS) dans le détail d'un
 * message automatique. Clé = canal (ex. `email`, `sms`).
 *
 * @example notificationChannelToggleTestId('email') // 'notification-channel-email'
 */
export const notificationChannelToggleTestId = (channel: string, sequenceIndex?: number): string =>
  sequenceIndex === undefined
    ? `notification-channel-${channel}`
    : // Événement séquencé : un même canal peut apparaître sur plusieurs lignes (ex. deux
      // relances e-mail), l'index garde le `data-testid` unique dans la modale.
      `notification-channel-${channel}-${sequenceIndex}`;

/**
 * `data-testid` d'une entrée principale du menu latéral (`app-sidebar`). Dérivé de
 * la route (ex. `/website` → `sidebar-nav-website`). Sert à filmer le point de
 * départ « où trouver l'endroit » de chaque démo.
 *
 * @example sidebarNavTestId('/website') // 'sidebar-nav-website'
 */
export const sidebarNavTestId = (url: string): string => `sidebar-nav${url.replace(/\//g, '-')}`;

/**
 * `data-testid` d'un sous-item du menu latéral (`app-sidebar`). Dérivé de la route
 * (ex. `/website/settings` → `sidebar-sub-website-settings`). Préfixe distinct de
 * `sidebarNavTestId` pour éviter toute collision avec l'entrée principale.
 *
 * @example sidebarSubTestId('/marketing/campaigns') // 'sidebar-sub-marketing-campaigns'
 */
export const sidebarSubTestId = (url: string): string => `sidebar-sub${url.replace(/\//g, '-')}`;

/**
 * `data-testid` d'une carte du hub des réglages du site (`/website/settings`).
 * Clé = section (`branding` / `seo` / `redirects` / `tracking`).
 *
 * @example websiteSettingsCardTestId('seo') // 'website-settings-card-seo'
 */
export const websiteSettingsCardTestId = (key: string): string => `website-settings-card-${key}`;
